import { Button, DatePicker, Select, Space } from "antd";
import Search from "antd/es/input/Search";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import AddIcon from "../../../assets/icons/AddIcon";
import DownloadIcon from "../../../assets/icons/DownloadIcon";
import UploadIconV2 from "../../../assets/icons/UploadIconV2";
import NetworkTable from "../../../components/NP/Network/NetworkTable";
import IconButton from "../../../components/common/IconButton";
import Subheader from "../../../components/ui/Subheader";
import { ArrayOption, selectSearchOption } from "../../../helpers/siteConfig";
import useDebounce from "../../../hooks/useDebounce";
import useExcelDownload from "../../../hooks/useExcelDownload";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import AdminLayout from "../../../layouts/AdminLayout";
import {
  getAllAreaAsync,
  getAllRegionAsync,
  getAllTerritoryAsync,
} from "../../../store/features/NP/geoInfo/geoAPI";
import { getAllSupplierAsync } from "../../../store/features/NP/supplier/supplierAPI";
import { useAppDispatch, useAppSelector } from "../../../store/store";
import { SupplierGetAllParams } from "../../../types/redux/NP/supplier";
import { stringToArray } from "../../../utils/array";
import { AddZero } from "../../../utils/number";

export default function Network() {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const path = stringToArray(location.pathname, "/")?.[0];
  const [status, setStatus] = useState<string>(
    (searchParams.get("status") as string)
      ? (searchParams.get("status") as string)
      : (searchParams.get("pg") as string)
      ? `pg-${searchParams.get("pg")}`
      : "",
  );
  const { login } = useAppSelector((state) => state.login);
  const { regions, areas, territorys } = useAppSelector((state) => state.npGeo);
  const { allSupplier } = useAppSelector((state) => state.supplier);
  const [regionId, setRegionId] = useState<string | undefined>(undefined);
  const [areaId, setAreaId] = useState<string | undefined>(undefined);
  const [territoryId, setTerritoryId] = useState(undefined);
  const [supplierId, setSupplierId] = useState<string | undefined>(undefined);
  const [downloadClicked, setDownloadClicked] = useState(false);
  const { excelLoading, downloadExcel, handleExcelLoading } =
    useExcelDownload("Tower-List-Data");

  const { open, toggle, handleOpen } = useModal();
  const { handleSetFirstPage, ...rest } = useTable();

  const { towersCount, allTowers, loading } = useAppSelector(
    (state) => state.network,
  );
  const { keyword, handleInputChange, handleEnterValue } = useDebounce();
  const {
    keyword: siteCode,
    handleInputChange: handleInputChange2,
    handleEnterValue: handleEnterValue2,
  } = useDebounce();

  const [today, setToday] = useState(true);
  const [date, setDate] = useState({
    from: "",
    to: "",
  });

  // const startDate = searchParams.get("startDate") || undefined;
  // const endDate = searchParams.get("endDate") || undefined;

  const handleSiteLocator = (value: string) => {
    setStatus(value);
    handleSetFirstPage(true);
  };

  useEffect(() => {
    dispatch(getAllRegionAsync({}));
  }, [dispatch]);

  useEffect(() => {
    if (regionId)
      dispatch(
        getAllAreaAsync({
          params: {
            region: regionId,
          },
        }),
      );
  }, [dispatch, regionId]);

  useEffect(() => {
    if (areaId)
      dispatch(
        getAllTerritoryAsync({
          params: {
            area: areaId,
          },
        }),
      );
  }, [dispatch, areaId]);

  useEffect(() => {
    if (login?.role === "region") {
      setRegionId(login?.region);
      setAreaId(login?.area as string);
    } else if (login?.role === "finance") {
      setRegionId(login?.region);
    }
  }, [login]);

  // console.log(login?.role);

  const supplierData = useCallback(() => {
    const params: SupplierGetAllParams = {
      // limit: limit,
      // skip: limit * (page - 1),
    };

    const searchParams: SupplierGetAllParams = {
      supplierName: keyword,
    };

    if (
      login?.role === "region" ||
      login?.role === "finance" ||
      login?.role === "vendor"
    ) {
      params.region = login?.region;
      searchParams.region = login?.region;
    }

    dispatch(
      getAllSupplierAsync({
        params: keyword ? searchParams : params,
      }),
    );
  }, [dispatch, keyword, login?.region, login?.role]);

  useEffect(() => {
    supplierData();
  }, [supplierData]);

  // const [isClicked, setIsClicked] = useState(false);
  // const [siteCodeVal, setSiteCodeVal] = useState<string | undefined>(undefined);
  // const [towerNameVal, setTowerNameVal] = useState<string | undefined>(
  //   undefined
  // );

  useEffect(() => {
    if (keyword || siteCode) {
      handleSetFirstPage(true);
      // setSiteCodeVal(siteCode);
      // setTowerNameVal(keyword);
      // setIsClicked(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, siteCode]);

  const handleClearFilter = () => {
    if (login?.role !== "region" && login?.role !== "finance") {
      setRegionId(undefined);
    }
    if (login?.role !== "region") {
      setAreaId(undefined);
    }
    setTerritoryId(undefined);
    setSupplierId(undefined);
    // setSiteCodeVal("");
    // setTowerNameVal("");
    // setIsClicked(true);
    setStatus("");
    handleEnterValue2("");
    handleEnterValue("");
    setDate({
      from: "",
      to: "",
    });
  };

  return (
    <AdminLayout
      title="Networks"
      headerTitle="Networks"
      headerChildren={
        <Space wrap>
          <IconButton
            className="!bg-[#FC8415] !hover:bg[#FC8415] !text-white !border-[#FC8415]"
            onClick={() =>
              handleOpen({
                type: "IMPORT",
              })
            }
          >
            <div className="flex items-center gap-x-2">
              <UploadIconV2 />
              <span>Import</span>
            </div>
          </IconButton>
          <Button
            loading={excelLoading}
            className="btn-status !bg-status-info-btn"
            icon={<DownloadIcon />}
            onClick={() => setDownloadClicked(true)}
          >
            Export
          </Button>
          <Link to={`/${path}/networks/add-tower`}>
            <IconButton type="primary" nextIcon={<AddIcon />}>
              Add New Tower
            </IconButton>
          </Link>
        </Space>
      }
    >
      <section className="mt-7">
        <Subheader
          title="Tower List"
          subTitle={`Total row: ${AddZero(towersCount || 0)}`}
        >
          <Space wrap>
            {/* <Search
              placeholder="Search by site code"
              onSearch={(val) => {
                handleEnterValue2(val);
              }}
              onChange={(e) => {
                handleInputChange2(e);
              }}
              value={siteCode}
              className="custom-search-input"
            /> */}

            <Search
              // value={keyword}
              placeholder="Search by tower name"
              onSearch={(val) => {
                handleEnterValue(val);
              }}
              onChange={(e) => {
                handleInputChange(e);
              }}
              className="custom-search-input"
            />
            <Select
              value={regionId}
              placeholder="Select cluster"
              allowClear
              showSearch
              filterOption={selectSearchOption}
              options={ArrayOption(regions, "regionName", "_id", true)}
              onChange={(val) => {
                setRegionId(val);
                setAreaId(undefined);
                setTerritoryId(undefined);
              }}
              className="min-w-[120px]"
              disabled={login?.role === "region" || login?.role === "finance"}
            />
            <Select
              placeholder="Select zone"
              allowClear
              showSearch
              filterOption={selectSearchOption}
              options={ArrayOption(areas, "areaName", "_id", true)}
              onChange={(val) => {
                setAreaId(val);
                setTerritoryId(undefined);
              }}
              value={areaId}
              className="min-w-[120px]"
              disabled={login?.role === "region" || !regionId}
            />
            <Select
              placeholder="Maintenance point "
              allowClear
              showSearch
              filterOption={selectSearchOption}
              options={ArrayOption(territorys, "teritoryName", "_id", true)}
              onChange={(val) => {
                setTerritoryId(val);
              }}
              value={territoryId}
              disabled={!areaId}
              className="min-w-[120px]"
              optionFilterProp="children"
            />

            <Select
              placeholder="Vendor"
              allowClear
              showSearch
              filterOption={selectSearchOption}
              options={ArrayOption(allSupplier, "supplierName", "_id", true)}
              onChange={(val: any) => {
                const selectedSupplier = allSupplier?.find(
                  (item) => item._id === val,
                );

                const region = selectedSupplier?.region?._id;
                setSupplierId(val);
                setRegionId(region);
              }}
              value={supplierId}
              // disabled={!areaId}
              className="min-w-[140px]"
              optionFilterProp="children"
            />
            <DatePicker.RangePicker
              format="YYYY-MM-DD"
              // size={innerWidth > 768 ? "large" : "middle"}
              // style={{ width: innerWidth > 768 ? "270px" : "240px" }}
              className="date-range"
              // popupClassName="one-month-date-range"
              value={[
                date?.from ? dayjs(date?.from, "YYYY-MM-DD") : null,
                date?.to ? dayjs(date?.to, "YYYY-MM-DD") : null,
              ]}
              onChange={(val) => {
                setDate({
                  from: val?.[0]?.format("YYYY-MM-DD") || "",
                  to: val?.[1]?.format("YYYY-MM-DD") || "",
                });

                setToday(val ? false : true);
              }}
            />

            <Select
              showSearch
              placeholder="Select PG Status"
              optionFilterProp="children"
              onChange={handleSiteLocator}
              filterOption={selectSearchOption}
              defaultValue="All"
              value={status || "All"}
              allowClear={status ? true : false}
              onClear={() => setStatus("")}
              className="min-w-[10rem]"
              options={[
                {
                  value: "pending",
                  label: "Did not start",
                },
                {
                  value: "start",
                  label: "Journey Started",
                },
                {
                  value: "reached",
                  label: "Reached Location",
                },
                {
                  value: "finished",
                  label: "Task Solved",
                },
                {
                  value: "unrespond",
                  label: "Unrespond",
                },
                {
                  value: "pg-pending",
                  label: "PG yet to run",
                },
                {
                  value: "pg-start",
                  label: "PG Running",
                },
                {
                  value: "pg-stop",
                  label: "PG Stopped",
                },
                {
                  value: "missCall",
                  label: "Missed Call",
                },
              ]}
            />
            <Button type="primary" onClick={handleClearFilter}>
              Clear Filter
            </Button>

            {/* <Button className="!text-white !bg-[#8897AE] !border-[#8897AE] !px-6">
              Filter
            </Button>
            <Select
              showSearch
              placeholder="Select"
              optionFilterProp="children"
              // onChange={handleRequestChange}
              filterOption={selectSearchOption}
              defaultValue={"This Month"}
              // value={status || "All"}
              allowClear
              className="min-w-[10rem]"
              options={[
                {
                  value: "This Month",
                  label: "This Month",
                },
              ]}
            /> */}
          </Space>
        </Subheader>

        <NetworkTable
          keyword={keyword}
          siteCode={siteCode}
          status={status}
          handleSetFirstPage={handleSetFirstPage}
          openImport={open}
          toggleImport={toggle}
          filter={{
            regionId,
            areaId,
            territoryId,
            supplierId,
            date,
            today,
          }}
          regions={regions}
          downloadExcel={downloadExcel}
          handleExcelLoading={handleExcelLoading}
          downloadClicked={downloadClicked}
          setDownloadClicked={setDownloadClicked}
          handleEnterValue2={handleEnterValue2}
          handleInputChange2={handleInputChange2}
          setRegionId={setRegionId}
          setAreaId={setAreaId}
          setTerritoryId={setTerritoryId}
          {...rest}
        />
      </section>
    </AdminLayout>
  );
}
